/**
 * CollapsibleDatasetDetails - Collapsible dataset metadata display
 * 
 * CHANGE HISTORY:
 * - 251201_0240 danielbeer: Created collapsible details component
 */

import React, { useState } from 'react';
import type { Dataset } from '../types/DatasetTypes';

export interface CollapsibleDatasetDetailsProps {
  dataset: Dataset;
  onEdit?: () => void;
  onExport?: () => void;
  onDelete?: () => void;
}

export function CollapsibleDatasetDetails({
  dataset,
  onEdit,
  onExport,
  onDelete
}: CollapsibleDatasetDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✓';
      case 'syncing': return '⟳';
      case 'error': return '⚠️';
      case 'draft': return '📝';
      default: return '○';
    }
  };
  
  return (
    <div className="dataset-details-collapsible">
      {/* Summary - Always Visible */}
      <div 
        className="dataset-summary"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="disclosure-triangle">
          {isExpanded ? '▾' : '▸'}
        </span>
        <div className="dataset-summary-content">
          <div className="dataset-summary-title">
            <span className="dataset-name">{dataset.name}</span>
            {!dataset.isUserDataset && (
              <span className="badge app-badge">App</span>
            )}
          </div>
          {dataset.description && (
            <p className="dataset-description">{dataset.description}</p>
          )}
        </div>
      </div>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="dataset-details-expanded">
          {/* Status and Counts */}
          <div className="details-row">
            <div className="detail-item">
              <span className="detail-label">Status:</span>
              <span className={`status-badge status-${dataset.status}`}>
                {getStatusIcon(dataset.status)} {dataset.status}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Sources:</span>
              <span className="detail-value">{dataset.sources?.length || 0}</span>
            </div>
            {dataset.fields && (
              <div className="detail-item">
                <span className="detail-label">Fields:</span>
                <span className="detail-value">{dataset.fields.length}</span>
              </div>
            )}
            {dataset.cacheConfig && (
              <div className="detail-item">
                <span className="detail-label">Cache:</span>
                <span className="detail-value">
                  {Math.floor(dataset.cacheConfig.ttl / 60)} min
                </span>
              </div>
            )}
          </div>
          
          {/* Timestamps */}
          <div className="details-row">
            <div className="detail-item">
              <span className="detail-label">Created:</span>
              <span className="detail-value">
                {new Date(dataset.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Updated:</span>
              <span className="detail-value">
                {new Date(dataset.updatedAt).toLocaleString()}
              </span>
            </div>
            {dataset.lastSync && (
              <div className="detail-item">
                <span className="detail-label">Last Sync:</span>
                <span className="detail-value">
                  {new Date(dataset.lastSync).toLocaleString()}
                </span>
              </div>
            )}
          </div>
          
          {/* Tags */}
          {dataset.tags && dataset.tags.length > 0 && (
            <div className="details-row">
              <div className="detail-item full-width">
                <span className="detail-label">Tags:</span>
                <div className="tags-list">
                  {dataset.tags.map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Sync Error */}
          {dataset.syncError && (
            <div className="details-row">
              <div className="detail-item full-width error">
                <span className="detail-label">Error:</span>
                <span className="error-message">{dataset.syncError}</span>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="details-actions">
            {dataset.isUserDataset && onEdit && (
              <button onClick={onEdit} className="btn btn-secondary">
                ✏️ Edit
              </button>
            )}
            {onExport && (
              <button onClick={onExport} className="btn btn-secondary">
                📤 Export
              </button>
            )}
            {dataset.isUserDataset && onDelete && (
              <button onClick={onDelete} className="btn btn-danger">
                🗑️ Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
